// frontend/src/context/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        removeToast,
        clearToasts,
        success: (message, duration) => showToast(message, "success", duration),
        error: (message, duration) => showToast(message, "error", duration),
        warning: (message, duration) => showToast(message, "warning", duration),
        info: (message, duration) => showToast(message, "info", duration),
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

// ToastContainer Component with Font Awesome
export const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  const getStyles = (type) => {
    const styles = {
      success: {
        background: "#dcfce7",
        color: "#16a34a",
        border: "1px solid #bbf7d0",
        icon: faCheckCircle,
      },
      error: {
        background: "#fee2e2",
        color: "#dc2626",
        border: "1px solid #fecaca",
        icon: faExclamationCircle,
      },
      warning: {
        background: "#fef3c7",
        color: "#d97706",
        border: "1px solid #fde68a",
        icon: faExclamationTriangle,
      },
      info: {
        background: "#dbeafe",
        color: "#2563eb",
        border: "1px solid #bfdbfe",
        icon: faInfoCircle,
      },
    };
    return styles[type] || styles.info;
  };

  return (
    <div className="toast-container">
      <style jsx>{`
        .toast-container {
          position: fixed;
          top: 1rem;
          right: 0.75rem;
          left: 0.75rem;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-width: 400px;
          margin: 0 auto;
          pointer-events: none;
        }

        @media (min-width: 640px) {
          .toast-container {
            left: auto;
            right: 1rem;
            margin: 0;
          }
        }

        .toast-item {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          animation: toastSlideIn 0.3s ease;
          pointer-events: auto;
          min-height: 52px;
        }

        .toast-item .toast-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .toast-item .toast-message {
          flex: 1;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.4;
        }

        .toast-item .toast-close {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          opacity: 0.6;
          padding: 0.25rem;
          transition: opacity 0.2s;
          min-height: 36px;
          min-width: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toast-item .toast-close:hover {
          opacity: 1;
        }

        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .toast-item {
            animation: none;
          }
        }
      `}</style>

      {toasts.map((toast) => {
        const style = getStyles(toast.type);
        return (
          <div
            key={toast.id}
            className="toast-item"
            style={{
              background: style.background,
              color: style.color,
              border: style.border,
            }}
          >
            <FontAwesomeIcon icon={style.icon} className="toast-icon" />
            <span className="toast-message">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="toast-close"
              aria-label="Dismiss notification"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        );
      })}
    </div>
  );
};